import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './Button';
import { IconButton } from './IconButton';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  siblingCount?: number;
  className?: string;
}

function buildPageList(currentPage: number, totalPages: number, siblings: number): Array<number | 'ellipsis'> {
  if (totalPages <= 1) return [1];
  const range: Array<number | 'ellipsis'> = [];
  const first = 1;
  const last = totalPages;
  const left = Math.max(currentPage - siblings, first + 1);
  const right = Math.min(currentPage + siblings, last - 1);

  range.push(first);
  if (left > first + 1) range.push('ellipsis');
  for (let i = left; i <= right; i++) range.push(i);
  if (right < last - 1) range.push('ellipsis');
  if (last > first) range.push(last);
  return range;
}

export function Pagination({ currentPage, totalPages, onPageChange, siblingCount = 1, className = '' }: PaginationProps) {
  const safeTotal = Math.max(totalPages, 1);
  const page = Math.min(Math.max(currentPage, 1), safeTotal);
  const canPrev = page > 1;
  const canNext = page < safeTotal;
  const pages = buildPageList(page, safeTotal, siblingCount);

  return (
    <nav
      aria-label="Pagination"
      className={['flex items-center gap-1', className].filter(Boolean).join(' ')}
    >
      <IconButton
        label="Previous page"
        icon={<ChevronLeft className="w-4 h-4" />}
        size="sm"
        variant="secondary"
        disabled={!canPrev}
        onClick={() => canPrev && onPageChange(page - 1)}
      />
      {pages.map((entry, idx) =>
        entry === 'ellipsis' ? (
          <span key={`e-${idx}`} aria-hidden="true" className="px-2 text-surface-500 select-none">
            …
          </span>
        ) : (
          <Button
            key={entry}
            size="sm"
            variant={entry === page ? 'primary' : 'secondary'}
            aria-current={entry === page ? 'page' : undefined}
            onClick={() => onPageChange(entry)}
          >
            {entry}
          </Button>
        ),
      )}
      <IconButton
        label="Next page"
        icon={<ChevronRight className="w-4 h-4" />}
        size="sm"
        variant="secondary"
        disabled={!canNext}
        onClick={() => canNext && onPageChange(page + 1)}
      />
    </nav>
  );
}
